import React, { useState } from 'react';
import axios from '../../config/axiosConfig';
import { MdArrowBack, MdCloudUpload, MdCheckCircle, MdError, MdDelete } from 'react-icons/md';

const AddGallery = ({ onBack, onGalleryAdded }) => {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [date, setDate] = useState(new Date().toISOString().split('T')[0]); // Default to today
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState(null); // 'success' | 'error' | null

    const handleImageChange = (e) => {
        if (e.target.files) {
            setImages((prev) => [...prev, ...Array.from(e.target.files)]);
        }
    };

    const removeImage = (index) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (images.length === 0) {
            alert("Please select at least one image.");
            return;
        }

        setLoading(true);
        setStatus(null);

        const formData = new FormData();
        formData.append('title', title);
        formData.append('description', description);
        formData.append('created_at', new Date(date).toISOString());
        
        // Append all selected files
        images.forEach((image) => {
            formData.append('images', image);
        });

        try {
            await axios.post('nai-gallery/', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            setStatus('success');
            // Reset form
            setTitle('');
            setDescription('');
            setImages([]);
            setDate(new Date().toISOString().split('T')[0]);
            // Notify parent to refresh list if needed
            if (onGalleryAdded) onGalleryAdded();
        } catch (error) {
            console.error("Error adding gallery:", error);
            setStatus('error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-6 max-w-3xl mx-auto">
            <div className="flex items-center mb-6">
                <button 
                    onClick={onBack}
                    className="mr-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
                >
                    <MdArrowBack className="text-gray-600 w-6 h-6" />
                </button>
                <h2 className="text-2xl font-bold text-gray-800">Create New Gallery (Album)</h2>
            </div>
            
            {status === 'success' && (
                <div className="mb-6 bg-green-50 border-l-4 border-green-500 p-4 rounded-md flex items-center">
                    <MdCheckCircle className="text-green-500 w-6 h-6 mr-3" />
                    <div>
                        <p className="font-medium text-green-700">Success!</p>
                        <p className="text-sm text-green-600">Gallery created successfully.</p>
                    </div>
                </div>
            )}

            {status === 'error' && (
                <div className="mb-6 bg-red-50 border-l-4 border-red-500 p-4 rounded-md flex items-center">
                     <MdError className="text-red-500 w-6 h-6 mr-3" />
                     <div>
                        <p className="font-medium text-red-700">Error</p>
                        <p className="text-sm text-red-600">Failed to create gallery. Please try again.</p>
                     </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Gallery Title</label>
                        <input
                            type="text"
                            required
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                            placeholder="e.g., CR Workshop - Krishnagiri"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Date</label>
                        <input
                            type="date"
                            required
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                        />
                    </div>
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        rows={3}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 border p-2"
                        placeholder="Optional description"
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium text-gray-700">Images</label>
                    <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-blue-400 transition-colors">
                        <div className="space-y-1 text-center">
                            <MdCloudUpload className="mx-auto h-12 w-12 text-gray-400" />
                            <div className="flex text-sm text-gray-600">
                                <label htmlFor="file-upload" className="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                                    <span>Upload files</span>
                                    <input 
                                        id="file-upload" 
                                        name="file-upload" 
                                        type="file" 
                                        className="sr-only" 
                                        onChange={handleImageChange} 
                                        accept="image/*" 
                                        multiple // Allow multiple files
                                        required={images.length === 0}
                                    />
                                </label>
                                <p className="pl-1">or drag and drop</p>
                            </div>
                            <p className="text-xs text-gray-500">PNG, JPG, GIF up to 5MB each</p>
                        </div>
                    </div>
                    
                    {/* Selected Images Preview List */}
                    {images.length > 0 && (
                        <div className="mt-4 grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                            {images.map((file, index) => (
                                <div key={index} className="relative group">
                                    <div className="w-full h-24 bg-gray-100 rounded-lg overflow-hidden border">
                                        {/* Simple preview (optional, using URL.createObjectURL for better preview) */}
                                        <div className="w-full h-full flex items-center justify-center text-xs text-gray-500 p-1 break-all">
                                            {file.name}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeImage(index)}
                                        className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
                                    >
                                        <MdDelete className="w-3 h-3" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                     {images.length > 0 && (
                        <p className="mt-2 text-sm text-gray-500 text-right">{images.length} images selected</p>
                    )}
                </div>

                <div className="flex justify-end">
                    <button
                        type="submit"
                        disabled={loading}
                        className={`inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${loading ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                        {loading ? 'Creating Gallery...' : 'Create Gallery'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AddGallery;
